/**
 * Notification Preferences Management - 3-level system: Global → Foundation → Fund
 */

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Space, Row, Col, Card, Input, Switch, Collapse, Spin } from "antd";
import { SearchOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { PREFERENCE_TYPES, PENDO_IDS, GLOBAL_TARGET_ID } from "../constant/NotificationPreferenceTypes";
import { WHITE, PRIMARY_600, GRAY_500, GRAY_200, GRAY_50 } from "../constant/colors";
import ErrorMessage from "../component/customComponent/customMessages/ErrorMessage";
import SuccessMessage from "../component/customComponent/customMessages/SuccessMessage";
import { NoFundsFoundIcon } from "../assets/svg/NoFundsFoundIcon";
import { SettingsIcon } from "../assets/svg/SettingsIcon";
import { getNotificationPreferences, updateNotificationPreference, getNotificationsAPI } from "../api/api";
import { WaitList, Close, Open, WaitListOnly } from "../component/Tags";
import { selectUser, selectEnvironment, selectNotificationDisplayFilter } from "../store/selector";
import { trackPendoEvent } from "../utils/pendo/pendoUtils";
import ACTIONS from "../store/action";
import { applyNotificationFilter, getUnreadCount } from "../utils/notificationFilters";
import { clearCache, CACHE_KEYS } from "../utils/cache";

const { Text } = Typography;
const { Panel } = Collapse;

const NotificationSettingsContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 12px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderSection = styled.div`
  flex-shrink: 0;
  padding: 8px 4px;
`;

const BreadcrumbSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 16px;
`;

const BreadcrumbItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: "Open Sans";
  font-size: 18px;
  font-weight: 600;
  color: #000000;
  cursor: pointer;

  &:hover {
    color: ${PRIMARY_600};
  }
`;

const BreadcrumbSeparator = () => (
  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 1L8 4L2 7" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BreadcrumbCurrent = styled.div`
  font-family: "Open Sans";
  font-size: 18px;
  font-weight: 600;
  color: #000000;
`;

const PageDescription = styled(Text)`
  font-size: 14px;
  color: ${GRAY_500};
  display: block;
`;

const SectionCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 12px;
  border: 1px solid #e4e7ec;
  width: 100%;
  background: ${GRAY_50};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: height 0.2s ease-out;
  ${(props) => (props.$isCollapsed ? "height: auto;" : "flex: 1; height: calc(100vh - 300px);")}

  .ant-card-body {
    padding: 20px;
    background: ${GRAY_50};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: flex 0.2s ease-out;
    ${(props) => (props.$isCollapsed ? "flex: none;" : "flex: 1;")}
  }
`;

const GlobalToggleSection = styled.div`
  padding: 16px;
  background: ${WHITE};
  border-bottom: 1px solid ${GRAY_200};
  margin-bottom: 16px;
  width: 100%;
  flex-shrink: 0;
`;

const GlobalToggleRow = styled(Row)`
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const TitleDescriptionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
`;

const OpenFundsTitle = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const OpenFundsDescription = styled(Text)`
  font-size: 14px;
  color: ${GRAY_500};
  margin: 0;
`;

const ToggleColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  min-width: 60px;
`;

const ToggleLabel = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  color: #000000;
  text-align: center;
`;

const SearchSection = styled.div`
  margin-bottom: 10px;
`;

const SearchInput = styled(Input)`
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  width: 100%;
  height: 53px;
  font-size: 14px;

  &:focus {
    border-color: ${PRIMARY_600};
    box-shadow: 0 0 0 2px rgba(16, 24, 40, 0.1);
  }

  &::placeholder {
    font-size: 14px;
    color: ${GRAY_500};
  }
`;

const FoundationsList = styled.div`
  width: 100%;
  flex: 1;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const StyledCollapse = styled(Collapse)`
  background: transparent;
  border: none;
  flex: 1;
  overflow: hidden;

  .ant-collapse-item {
    margin-bottom: 0;
    border: none !important;
    background: #f9fafb;
    border-radius: 8px;
    border-bottom: 1px solid ${GRAY_200} !important;

    &:last-child {
      border-bottom: none !important;
    }
  }

  .ant-collapse-header {
    background: #f9fafb;
    border-radius: 8px !important;
    padding: 16px !important;
    border: none !important;
  }

  .ant-collapse-content {
    background: #f9fafb;
    border: none !important;
    border-radius: 0 0 8px 8px;
  }

  .ant-collapse-content-box {
    padding: 0 16px 16px 16px !important;
    border-top: 1px solid #e4e7ec;
    margin-top: 0;
  }
`;

const FoundationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  cursor: pointer;
`;

const FoundationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FoundationTitle = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: #000000;
`;

const ToggleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  min-width: 120px;
  flex-shrink: 0;
`;

const ToggleWithLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  min-width: 60px;
  flex-shrink: 0;
`;

const StatusText = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  color: ${GRAY_500};
  white-space: nowrap;
`;

const FundItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  padding-left: 25px;
  gap: 12px;
`;

const FundName = styled(Text)`
  font-size: 14px;
  color: #000000;
  font-weight: 400;
`;

const FundNameArea = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationSettings = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const account = useSelector(selectEnvironment);
  const displayFilter = useSelector(selectNotificationDisplayFilter);

  const [preferences, setPreferences] = useState({
    global: { inApp: false, email: false },
    foundations: [],
    funds: []
  });
  const [, setOriginalPreferences] = useState({
    global: { inApp: false, email: false },
    foundations: [],
    funds: []
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [updating, setUpdating] = useState({});
  const [activeKeys, setActiveKeys] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isAdvancedCollapsed, setIsAdvancedCollapsed] = useState(true);

  // Pendo tracking helper functions
  const trackGlobalToggle = (type, enabled) => {
    try {
      const eventName =
        type === "inApp" ? "Notification_Preferences_GlobalToggleInApp" : "Notification_Preferences_GlobalToggleEmail";
      trackPendoEvent(dispatch, eventName, {
        enabled,
        username: user?.email,
        account
      });
    } catch (error) {
      console.error("Error dispatching global toggle Pendo event:", error);
    }
  };

  const trackFoundationToggle = (type, foundationName, enabled) => {
    try {
      const eventName =
        type === "inApp"
          ? "Notification_Preferences_FoundationToggle_InApp"
          : "Notification_Preferences_FoundationToggle_Email";
      trackPendoEvent(dispatch, eventName, {
        foundationName,
        enabled,
        username: user?.email,
        account
      });
    } catch (error) {
      console.error("Error dispatching foundation toggle Pendo event:", error);
    }
  };

  const trackFundToggle = (type, fundName, foundationName, enabled) => {
    try {
      const eventName =
        type === "inApp" ? "Notification_Preferences_FundToggle_InApp" : "Notification_Preferences_FundToggle_Email";
      trackPendoEvent(dispatch, eventName, {
        fundName,
        foundationName,
        enabled,
        username: user?.email,
        account
      });
    } catch (error) {
      console.error("Error dispatching fund toggle Pendo event:", error);
    }
  };

  const trackAdvancedSettingsToggle = (isOpening) => {
    try {
      const eventName = isOpening
        ? "Notification_Preferences_OpenAdvancedSettings"
        : "Notification_Preferences_CloseAdvanceSettings";
      trackPendoEvent(dispatch, eventName, {
        username: user?.email,
        account
      });
    } catch (error) {
      console.error("Error dispatching advanced settings toggle Pendo event:", error);
    }
  };

  const trackSearch = (searchTerm) => {
    try {
      trackPendoEvent(dispatch, "Notification_Preferences_Search", {
        searchTerm,
        username: user?.email,
        account
      });
    } catch (error) {
      console.error("Error dispatching search Pendo event:", error);
    }
  };

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        setIsLoading(true);
        const response = await getNotificationPreferences();
        const transformedResponse = {
          global: response.data.globalPreferences || { inApp: false, email: false },
          foundations: response.data.foundations || [],
          funds: [] // Add empty funds array as expected by frontend
        };
        setPreferences(transformedResponse);
        setOriginalPreferences(transformedResponse);
        setActiveKeys([]);
        setFilteredData(transformedResponse.foundations);
      } catch (error) {
        ErrorMessage(t("notification_preferences.message_fetch_error"));
        setPreferences({
          global: { inApp: false, email: false },
          foundations: [],
          funds: []
        });
        setOriginalPreferences({
          global: { inApp: false, email: false },
          foundations: [],
          funds: []
        });
        setActiveKeys([]);
        setFilteredData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreferences();
  }, [t]);

  // Helper function to compute effective foundation state considering inheritance from global
  const getEffectiveFoundationState = useCallback(
    (foundation, channel) => {
      // If foundation has explicit preference (true or false), use it
      if (foundation[channel] !== null) {
        return foundation[channel];
      }
      // If foundation has no preference (null), inherit from global
      // Global true means enabled, global null means disabled
      return preferences.global[channel] === true;
    },
    [preferences.global]
  );

  // Helper function to compute effective fund state considering inheritance from foundation
  const getEffectiveFundState = useCallback(
    (fund, foundation, channel) => {
      // If fund has explicit preference (true or false), use it
      if (fund[channel] !== null) {
        return fund[channel];
      }
      // If fund has no preference (null), inherit from effective foundation state
      return getEffectiveFoundationState(foundation, channel);
    },
    [getEffectiveFoundationState]
  );

  // Helper function to check if any fund in a foundation is enabled (for visual hierarchy)
  const hasAnyFundEnabled = useCallback(
    (foundation, channel) => {
      // Use the original foundation data to check if any fund is enabled, not the filtered data
      foundation = preferences.foundations.find((f) => f.id === foundation.id) || foundation;
      return foundation.funds?.some((fund) => getEffectiveFundState(fund, foundation, channel)) || false;
    },
    [getEffectiveFundState, preferences.foundations]
  );

  // Helper function to check if any foundation is enabled (for visual hierarchy)
  const hasAnyFoundationEnabled = useCallback(
    (channel) => {
      return preferences.foundations.some((foundation) => {
        // Check if foundation itself is enabled OR any of its funds are enabled
        const foundationEnabled = getEffectiveFoundationState(foundation, channel);
        const anyFundEnabled = hasAnyFundEnabled(foundation, channel);
        return foundationEnabled || anyFundEnabled;
      });
    },
    [preferences.foundations, getEffectiveFoundationState, hasAnyFundEnabled]
  );

  // Helper function to get visual state for foundation toggle (shows on if any fund is on)
  const getVisualFoundationState = useCallback(
    (foundation, channel) => {
      const foundationState = getEffectiveFoundationState(foundation, channel);
      const anyFundEnabled = hasAnyFundEnabled(foundation, channel);
      return foundationState || anyFundEnabled;
    },
    [getEffectiveFoundationState, hasAnyFundEnabled]
  );

  // Helper function to get visual state for global toggle (shows on if any foundation/fund is on)
  const getVisualGlobalState = useCallback(
    (channel) => {
      const globalState = preferences.global[channel] === true;
      const anyFoundationEnabled = hasAnyFoundationEnabled(channel);
      return globalState || anyFoundationEnabled;
    },
    [preferences.global, hasAnyFoundationEnabled]
  );

  const getFoundationStatus = useCallback(
    (foundation) => {
      // Use the original foundation data to calculate status, not the filtered data
      foundation = preferences.foundations.find((f) => f.id === foundation.id) || foundation;
      const totalFunds = foundation.funds.length;

      // Get foundation's own effective states
      const foundationInApp = getEffectiveFoundationState(foundation, "inApp");
      const foundationEmail = getEffectiveFoundationState(foundation, "email");

      // Check if foundation has explicit preferences vs inheriting from global
      const hasExplicitInApp = foundation.inApp !== null;
      const hasExplicitEmail = foundation.email !== null;

      // Get global states for comparison
      const globalInApp = preferences.global.inApp === true;
      const globalEmail = preferences.global.email === true;

      // Calculate effective fund states considering inheritance
      const effectiveFundStates = foundation.funds.map((fund) => ({
        inApp: getEffectiveFundState(fund, foundation, "inApp"),
        email: getEffectiveFundState(fund, foundation, "email")
      }));

      // Check if all funds are fully subscribed (both inApp and email ON)
      const fullySubscribedFunds = effectiveFundStates.filter((f) => f.inApp && f.email).length;

      // Check if all funds are fully unsubscribed (both inApp and email OFF)
      const fullyUnsubscribedFunds = effectiveFundStates.filter((f) => !f.inApp && !f.email).length;

      // Check for "Partially" conditions:
      // 1. Foundation's effective states are different
      if (foundationInApp !== foundationEmail) {
        return t("notification_preferences.status_partially_subscribed");
      }

      // 2. Foundation has mixed explicit vs inherited settings
      // If one channel is explicit and one inherits, check if they create different effective states
      if (hasExplicitInApp !== hasExplicitEmail) {
        // If foundation overrides one channel but inherits the other,
        // and this creates a different pattern than pure inheritance would
        const wouldInheritInApp = globalInApp;
        const wouldInheritEmail = globalEmail;

        // If the foundation's actual effective state differs from what pure inheritance would give
        if (foundationInApp !== wouldInheritInApp || foundationEmail !== wouldInheritEmail) {
          // Check if the result is fully subscribed despite the override
          if (foundationInApp && foundationEmail) {
            return t("notification_preferences.status_subscribed_all");
          }
          // Check if the result is fully unsubscribed despite the override
          if (!foundationInApp && !foundationEmail) {
            return t("notification_preferences.status_unsubscribed");
          }
          // Otherwise it's partially subscribed
          return t("notification_preferences.status_partially_subscribed");
        }
      }

      // 3. Foundation inherits from global but global has mixed states
      if (!hasExplicitInApp && !hasExplicitEmail && globalInApp !== globalEmail) {
        // Even if global has mixed states, check the actual effective result
        if (foundationInApp && foundationEmail) {
          return t("notification_preferences.status_subscribed_all");
        }
        if (!foundationInApp && !foundationEmail) {
          return t("notification_preferences.status_unsubscribed");
        }
        return t("notification_preferences.status_partially_subscribed");
      }

      // If foundation has consistent state, check fund states
      if (fullySubscribedFunds === totalFunds) {
        return t("notification_preferences.status_subscribed_all");
      }

      if (fullyUnsubscribedFunds === totalFunds) {
        return t("notification_preferences.status_unsubscribed");
      }

      return t("notification_preferences.status_partially_subscribed");
    },
    [t, getEffectiveFundState, getEffectiveFoundationState, preferences.global, preferences.foundations]
  );

  const getFundStatus = useCallback(
    (fund, foundation) => {
      const effectiveInApp = getEffectiveFundState(fund, foundation, "inApp");
      const effectiveEmail = getEffectiveFundState(fund, foundation, "email");

      if (effectiveInApp && effectiveEmail) {
        return t("notification_preferences.status_subscribed");
      }
      if (effectiveInApp || effectiveEmail) {
        return t("notification_preferences.status_partially_subscribed");
      }
      return t("notification_preferences.status_unsubscribed");
    },
    [t, getEffectiveFundState]
  );

  const getGlobalStatus = useCallback(() => {
    // Collect all funds across all foundations
    const allFunds = [];

    preferences.foundations.forEach((foundation) => {
      foundation.funds?.forEach((fund) => {
        allFunds.push({
          fund,
          foundation,
          effectiveInApp: getEffectiveFundState(fund, foundation, "inApp"),
          effectiveEmail: getEffectiveFundState(fund, foundation, "email")
        });
      });
    });

    if (allFunds.length === 0) {
      return t("notification_preferences.status_unsubscribed");
    }

    // Check if all funds are fully subscribed (both inApp and email ON)
    const fullySubscribedFunds = allFunds.filter((f) => f.effectiveInApp && f.effectiveEmail).length;

    // Check if all funds are fully unsubscribed (both inApp and email OFF)
    const fullyUnsubscribedFunds = allFunds.filter((f) => !f.effectiveInApp && !f.effectiveEmail).length;

    if (fullySubscribedFunds === allFunds.length) {
      return t("notification_preferences.status_subscribed_all");
    }

    if (fullyUnsubscribedFunds === allFunds.length) {
      return t("notification_preferences.status_unsubscribed");
    }

    return t("notification_preferences.status_partially_subscribed");
  }, [t, preferences.foundations, getEffectiveFundState]);

  const performSearch = useCallback((value, foundationData, preserveActiveKeys = false) => {
    if (!value || value.trim() === "") {
      const validFoundations = (foundationData || []).filter(
        (foundation) => foundation.name && foundation.name.trim() !== ""
      );
      setFilteredData(validFoundations);
      if (!preserveActiveKeys) {
        setActiveKeys([]); // Close all foundations when search is cleared (but not when refreshing data)
      }
      return;
    }

    const filtered = (foundationData || [])
      .filter((foundation) => {
        if (!foundation.name || foundation.name.trim() === "") {
          return false;
        }

        const foundationNameMatches = foundation.name.toLowerCase().includes(value.toLowerCase());
        const matchingFunds =
          foundation.funds?.filter((fund) => fund.name.toLowerCase().includes(value.toLowerCase())) || [];

        return foundationNameMatches || matchingFunds.length > 0;
      })
      .map((foundation) => {
        if (foundation.name.toLowerCase().includes(value.toLowerCase())) {
          return foundation;
        }

        const matchingFunds =
          foundation.funds?.filter((fund) => fund.name.toLowerCase().includes(value.toLowerCase())) || [];

        return {
          ...foundation,
          funds: matchingFunds
        };
      });

    setFilteredData(filtered);
    setActiveKeys(filtered.map((f) => f.id.toString()));
  }, []);

  // Helper function to refresh notification bell count after preference changes
  const refreshNotificationBellCount = useCallback(async () => {
    try {
      const res = await getNotificationsAPI();
      if (res && res.data && res.data.notifications) {
        const notifications = res.data.notifications;

        const filterKey = displayFilter || "unread";
        const filteredNotifications = applyNotificationFilter(notifications, filterKey);

        const unreadCount = getUnreadCount(filteredNotifications);

        dispatch(ACTIONS.setBadgeCounts("notifications", unreadCount));
      }
    } catch (error) {
      // Log an error and continue - this is not critical to the preference update
      console.error("Failed to refresh notification count:", error);
    }
  }, [dispatch, displayFilter]);

  const handleUpdatePreference = useCallback(
    async (preferenceType, targetId, channel, isEnabled) => {
      const updateKey = `${preferenceType}_${targetId}_${channel}`;

      if (updating[updateKey]) {
        return;
      }

      // Track Pendo events based on preference type
      if (preferenceType === PREFERENCE_TYPES.GLOBAL) {
        trackGlobalToggle(channel, isEnabled);
      } else if (preferenceType === PREFERENCE_TYPES.FOUNDATION) {
        const foundation = preferences.foundations.find((f) => f.id === targetId);
        if (foundation) {
          trackFoundationToggle(channel, foundation.name, isEnabled);
        }
      } else if (preferenceType === PREFERENCE_TYPES.FUND) {
        // Find the fund and its foundation
        let fundName = "";
        let foundationName = "";
        for (const foundation of preferences.foundations) {
          const fund = foundation.funds?.find((f) => f.id === targetId);
          if (fund) {
            fundName = fund.name;
            foundationName = foundation.name;
            break;
          }
        }
        if (fundName) {
          trackFundToggle(channel, fundName, foundationName, isEnabled);
        }
      }

      try {
        setUpdating((prev) => ({ ...prev, [updateKey]: true }));

        await updateNotificationPreference(preferenceType, targetId, channel, isEnabled);

        // Refresh preferences from backend to get the updated state
        const response = await getNotificationPreferences();
        const transformedResponse = {
          global: response.data.globalPreferences || { inApp: false, email: false },
          foundations: response.data.foundations || [],
          funds: []
        };
        setPreferences(transformedResponse);
        setOriginalPreferences(transformedResponse);
        performSearch(searchTerm, transformedResponse.foundations, true);

        if (channel === "inApp") {
          // Clear cache to force fresh fetch on next notification bell open
          clearCache(CACHE_KEYS.NOTIFICATIONS);

          // Don't await - we don't want the toggle UI to wait for the notification bell count to be refreshed
          refreshNotificationBellCount().catch((error) => {
            console.error("Error refreshing notification bell count:", error);
          });
        }

        SuccessMessage(t("notification_preferences.message_update_success"));
      } catch (error) {
        console.error("Error updating preference:", error);
        ErrorMessage(t("notification_preferences.message_update_error"));
        // Refresh preferences from backend to ensure consistency
        try {
          const response = await getNotificationPreferences();
          const transformedResponse = {
            global: response.data.globalPreferences || { inApp: false, email: false },
            foundations: response.data.foundations || [],
            funds: []
          };
          setPreferences(transformedResponse);
          setOriginalPreferences(transformedResponse);
          performSearch(searchTerm, transformedResponse.foundations, true);
          if (channel === "inApp") {
            // Clear cache to ensure consistency even on error recovery
            clearCache(CACHE_KEYS.NOTIFICATIONS);

            // Don't await - we don't want the toggle UI to wait for the notification bell count to be refreshed
            refreshNotificationBellCount().catch((error) => {
              console.error("Non-critical error refreshing notification count on recovery:", error);
            });
          }
        } catch (refreshError) {
          console.error("Error refreshing preferences:", refreshError);
          // If refresh fails, keep current state
        }
      } finally {
        setUpdating((prev) => ({ ...prev, [updateKey]: false }));
      }
    },
    [t, preferences.foundations, searchTerm, performSearch, refreshNotificationBellCount, updating]
  );

  const handleSearch = useCallback(
    (value) => {
      setSearchTerm(value);

      // Track search event only when user actually types something
      if (value && value.trim() !== "") {
        trackSearch(value);
      }

      // Auto-expand the card when user starts searching
      if (value && value.trim() !== "" && isAdvancedCollapsed) {
        setIsAdvancedCollapsed(false);
      }
      performSearch(value, preferences.foundations);
    },
    [performSearch, preferences.foundations, isAdvancedCollapsed]
  );

  useEffect(() => {
    if (searchTerm && searchTerm.trim() !== "") {
      performSearch(searchTerm, preferences.foundations);
    } else {
      const validFoundations = (preferences.foundations || []).filter(
        (foundation) => foundation.name && foundation.name.trim() !== ""
      );
      setFilteredData(validFoundations);
    }
  }, [preferences.foundations, searchTerm, performSearch]);

  if (isLoading) {
    return (
      <NotificationSettingsContainer data-pendo={PENDO_IDS.NOTIFICATION_PREFERENCES_PAGE}>
        <HeaderSection>
          <BreadcrumbSection>
            <BreadcrumbItem id="Notification_Preferences_BackToSettings" onClick={() => history.push("/settings")}>
              <SettingsIcon />
              Settings
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbCurrent>Notifications</BreadcrumbCurrent>
          </BreadcrumbSection>
          <PageDescription>{t("notification_preferences.page_description")}</PageDescription>
        </HeaderSection>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
          <Spin size="large" />
        </div>
      </NotificationSettingsContainer>
    );
  }

  return (
    <NotificationSettingsContainer data-pendo={PENDO_IDS.NOTIFICATION_PREFERENCES_PAGE}>
      <HeaderSection>
        <BreadcrumbSection>
          <BreadcrumbItem id="Notification_Preferences_BackToSettings" onClick={() => history.push("/settings")}>
            <SettingsIcon />
            Settings
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbCurrent>Notifications</BreadcrumbCurrent>
        </BreadcrumbSection>
        <PageDescription>{t("notification_preferences.page_description")}</PageDescription>
      </HeaderSection>

      <GlobalToggleSection>
        <GlobalToggleRow>
          <Col flex="1">
            <TitleDescriptionContainer>
              <OpenFundsTitle>{t("notification_preferences.open_funds_title")}</OpenFundsTitle>
              <OpenFundsDescription>{t("notification_preferences.open_funds_description")}</OpenFundsDescription>
            </TitleDescriptionContainer>
          </Col>
          <Col style={{ minWidth: "120px" }}>
            <Row gutter={20} justify="end">
              <Col>
                <ToggleColumn>
                  <ToggleLabel style={{ width: "100%", display: "block", textAlign: "right" }}>
                    {t("notification_preferences.in_app_label")}
                  </ToggleLabel>
                  <ToggleWithLabel>
                    <StatusText>{getGlobalStatus()}</StatusText>
                    <Switch
                      id="Notification_Preferences_GlobalToggleInApp"
                      checked={getVisualGlobalState("inApp")}
                      onChange={(checked) =>
                        handleUpdatePreference(PREFERENCE_TYPES.GLOBAL, GLOBAL_TARGET_ID, "inApp", checked)
                      }
                      loading={updating[`${PREFERENCE_TYPES.GLOBAL}_${GLOBAL_TARGET_ID}_inApp`]}
                      style={{ backgroundColor: getVisualGlobalState("inApp") ? PRIMARY_600 : undefined }}
                    />
                  </ToggleWithLabel>
                </ToggleColumn>
              </Col>
              <Col>
                <ToggleColumn>
                  <ToggleLabel>{t("notification_preferences.email_label")}</ToggleLabel>
                  <Switch
                    id="Notification_Preferences_GlobalToggleEmail"
                    checked={getVisualGlobalState("email")}
                    onChange={(checked) =>
                      handleUpdatePreference(PREFERENCE_TYPES.GLOBAL, GLOBAL_TARGET_ID, "email", checked)
                    }
                    loading={updating[`${PREFERENCE_TYPES.GLOBAL}_${GLOBAL_TARGET_ID}_email`]}
                    style={{ backgroundColor: getVisualGlobalState("email") ? PRIMARY_600 : undefined }}
                  />
                </ToggleColumn>
              </Col>
            </Row>
          </Col>
        </GlobalToggleRow>
      </GlobalToggleSection>

      <SectionCard $isCollapsed={isAdvancedCollapsed}>
        <div style={{ flexShrink: 0 }}>
          <Row
            id="Notification_Preferences_OpenAdvancedSettings"
            justify="space-between"
            align="middle"
            style={{
              marginBottom: isAdvancedCollapsed ? 0 : 10,
              cursor: "pointer",
              padding: "4px 0"
            }}
            onClick={() => {
              const willBeCollapsed = !isAdvancedCollapsed;
              trackAdvancedSettingsToggle(!willBeCollapsed); // Track if opening (true) or closing (false)
              setIsAdvancedCollapsed(willBeCollapsed);
            }}
          >
            <Col>
              <Text
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#000000",
                  margin: 0,
                  display: "block"
                }}
              >
                {t("notification_preferences.advanced_settings_title")}
              </Text>
            </Col>
            <Col>
              <div
                id="Notification_Preferences_AdvancedSettings_Arrow"
                style={{
                  padding: "4px 8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  color: "#000000",
                  fontSize: "14px"
                }}
              >
                {isAdvancedCollapsed ? <DownOutlined /> : <UpOutlined />}
              </div>
            </Col>
          </Row>

          <div
            style={{
              maxHeight: isAdvancedCollapsed ? "0px" : "120px",
              opacity: isAdvancedCollapsed ? 0 : 1,
              overflow: "hidden",
              transition: "max-height 0.3s ease-in-out, opacity 0.3s ease-in-out"
            }}
          >
            <Text style={{ fontSize: "14px", color: GRAY_500, display: "block", marginBottom: 10 }}>
              {t("notification_preferences.search_description")}
            </Text>

            <SearchSection id="Notification_Preferences_Search">
              <SearchInput
                allowClear
                placeholder={t("notification_preferences.search_placeholder")}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                prefix={<SearchOutlined style={{ color: GRAY_500 }} />}
              />
            </SearchSection>
          </div>
        </div>

        <FoundationsList
          style={{
            maxHeight: isAdvancedCollapsed ? "0px" : "1000px",
            opacity: isAdvancedCollapsed ? 0 : 1,
            overflow: isAdvancedCollapsed ? "hidden" : "auto",
            transition: "max-height 0.3s ease-in-out, opacity 0.3s ease-in-out"
          }}
        >
          {filteredData.length === 0 && searchTerm && searchTerm.trim() !== "" ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
              <Space direction="vertical" align="center" size={0}>
                <NoFundsFoundIcon />
                <Text
                  style={{
                    fontFamily: "Open Sans",
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "18px",
                    color: GRAY_500
                  }}
                >
                  {t("notification_preferences.no_funds_found")}
                </Text>
                <Text
                  style={{
                    fontFamily: "Open Sans",
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "18px",
                    color: GRAY_500
                  }}
                >
                  {t("notification_preferences.try_searching_again")}
                </Text>
              </Space>
            </div>
          ) : (
            <StyledCollapse activeKey={activeKeys} onChange={setActiveKeys} expandIconPosition="start">
              {filteredData.map((foundation) => (
                <Panel
                  key={foundation.id.toString()}
                  header={
                    <FoundationHeader>
                      <FoundationInfo>
                        <FoundationTitle>{foundation.name}</FoundationTitle>
                      </FoundationInfo>
                      <ToggleGroup
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <ToggleWithLabel>
                          <StatusText>{getFoundationStatus(foundation)}</StatusText>
                          <Switch
                            id={`Notification_Preferences_FoundationToggle_InApp_${foundation.id}`}
                            checked={getVisualFoundationState(foundation, "inApp")}
                            onChange={(checked) =>
                              handleUpdatePreference(PREFERENCE_TYPES.FOUNDATION, foundation.id, "inApp", checked)
                            }
                            loading={updating[`${PREFERENCE_TYPES.FOUNDATION}_${foundation.id}_inApp`]}
                            style={{
                              backgroundColor: getVisualFoundationState(foundation, "inApp") ? PRIMARY_600 : undefined
                            }}
                          />
                        </ToggleWithLabel>
                        <ToggleWithLabel>
                          <Switch
                            id={`Notification_Preferences_FoundationToggle_Email_${foundation.id}`}
                            checked={getVisualFoundationState(foundation, "email")}
                            onChange={(checked) =>
                              handleUpdatePreference(PREFERENCE_TYPES.FOUNDATION, foundation.id, "email", checked)
                            }
                            loading={updating[`${PREFERENCE_TYPES.FOUNDATION}_${foundation.id}_email`]}
                            style={{
                              backgroundColor: getVisualFoundationState(foundation, "email") ? PRIMARY_600 : undefined
                            }}
                          />
                        </ToggleWithLabel>
                      </ToggleGroup>
                    </FoundationHeader>
                  }
                >
                  {foundation.funds.map((fund) => (
                    <FundItem key={fund.id}>
                      <FundNameArea>
                        <Space align="center" size={8} wrap>
                          <FundName>{fund.name}</FundName>
                          {fund.status === "waitlist" && <WaitList />}
                          {fund.status === "closed" && <Close />}
                          {fund.status === "open" && <Open />}
                          {fund.status === "waitlist only" && <WaitListOnly />}
                        </Space>
                      </FundNameArea>
                      <ToggleGroup>
                        <ToggleWithLabel>
                          <StatusText>{getFundStatus(fund, foundation)}</StatusText>
                          <Switch
                            id={`Notification_Preferences_FundToggle_InApp_${fund.id}`}
                            checked={getEffectiveFundState(fund, foundation, "inApp")}
                            onChange={(checked) =>
                              handleUpdatePreference(PREFERENCE_TYPES.FUND, fund.id, "inApp", checked)
                            }
                            loading={updating[`${PREFERENCE_TYPES.FUND}_${fund.id}_inApp`]}
                            style={{
                              backgroundColor: getEffectiveFundState(fund, foundation, "inApp")
                                ? PRIMARY_600
                                : undefined
                            }}
                          />
                        </ToggleWithLabel>
                        <ToggleWithLabel>
                          <Switch
                            id={`Notification_Preferences_FundToggle_Email_${fund.id}`}
                            checked={getEffectiveFundState(fund, foundation, "email")}
                            onChange={(checked) =>
                              handleUpdatePreference(PREFERENCE_TYPES.FUND, fund.id, "email", checked)
                            }
                            loading={updating[`${PREFERENCE_TYPES.FUND}_${fund.id}_email`]}
                            style={{
                              backgroundColor: getEffectiveFundState(fund, foundation, "email")
                                ? PRIMARY_600
                                : undefined
                            }}
                          />
                        </ToggleWithLabel>
                      </ToggleGroup>
                    </FundItem>
                  ))}
                </Panel>
              ))}
            </StyledCollapse>
          )}
        </FoundationsList>
      </SectionCard>
    </NotificationSettingsContainer>
  );
};

export default NotificationSettings;
